"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "@digirank/ui";
import { Loader2, Mail, User as UserIcon, CheckCircle2, Lock } from "lucide-react";

// Display copy for each plan code. Mirrors www.digirankexpert.com/pricing
// exactly so the customer never sees one number on the marketing page and
// a different one on /signup. Caught live during the audit (B1 blocker):
// the previous Growth Starter $199 contradicted the new Starter $99.
//
// Legacy growth_* codes are kept here as aliases pointing at the new prices
// so signup links already in the wild don't break — they just resolve to
// the modern equivalent (growth_starter→starter, growth_pro→pro,
// growth_agency→agency).
const planNames: Record<string, string> = {
  starter: "Starter — $99/mo",
  agency: "Agency — $249/mo · 14-Day Free Trial",
  pro: "Pro — $499/mo · 14-Day Free Trial",
  scale: "Scale — $999/mo · 14-Day Free Trial",
  growth_starter: "Starter — $99/mo",
  growth_pro: "Pro — $499/mo · 14-Day Free Trial",
  growth_agency: "Agency — $249/mo · 14-Day Free Trial",
};

const planDetails: Record<string, { price: string; trial?: string }> = {
  starter: { price: "$99/mo" },
  agency: { price: "$249/mo after trial", trial: "Free for 14 days" },
  pro: { price: "$499/mo after trial", trial: "Free for 14 days" },
  scale: { price: "$999/mo after trial", trial: "Free for 14 days" },
  growth_starter: { price: "$99/mo" },
  growth_pro: { price: "$499/mo after trial", trial: "Free for 14 days" },
  growth_agency: { price: "$249/mo after trial", trial: "Free for 14 days" },
};

function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Default to "agency" — the lead tier on the marketing pricing page.
  // Legacy ?plan=growth_* URLs still work because the maps above include
  // those codes.
  const planParam = searchParams.get("plan") || "agency";
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
      
      const response = await fetch(`${apiUrl}/api/checkout/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          plan: planParam,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create checkout session");
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            Get Started with DigiRank
          </CardTitle>
          <p className="text-center text-gray-600 mt-2">
            {planNames[planParam] || "Choose your plan"}
          </p>
          {planDetails[planParam]?.trial && (
            <div className="mt-3 flex justify-center">
              <div className="inline-block rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700">
                ✨ {planDetails[planParam].trial} - No credit card charged until trial ends
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Plan Benefits */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">Automated SEO monitoring & fixes</span>
            </div>
            <div className="flex items-start gap-2 mb-2">
              <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">AI-powered content generation</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">Direct publishing to WordPress or Git</span>
            </div>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  className="pl-10"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="john@company.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  className="pl-10"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  minLength={6}
                />
              </div>
              <p className="text-xs text-gray-500">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  className="pl-10"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, confirmPassword: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Continue to Payment</>
              )}
            </Button>
          </form>

          <div className="text-center text-sm text-gray-600">
            <p>You'll be redirected to Stripe for secure payment</p>
            <p className="mt-2">Already have an account?{" "}
              <button
                onClick={() => router.push("/login")}
                className="text-purple-600 hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
      <SignupContent />
    </Suspense>
  );
}
